import { FormEvent, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { resetPassword } from "../features/settings/api";
import { useAuth } from "../features/auth/useAuth";

const MIN_PASSWORD_LENGTH = 8;

const ResetPassword = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canSubmit = useMemo(() => {
    return (
      currentPassword.trim().length > 0 &&
      newPassword.trim().length >= MIN_PASSWORD_LENGTH &&
      confirmPassword.trim().length >= MIN_PASSWORD_LENGTH &&
      !isSubmitting
    );
  }, [currentPassword, newPassword, confirmPassword, isSubmitting]);

  const disabledHint = useMemo(() => {
    if (isSubmitting) return null;
    if (!currentPassword.trim()) return "Enter your current (temporary) password.";
    if (newPassword.trim().length < MIN_PASSWORD_LENGTH)
      return `New password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
    if (confirmPassword.trim().length < MIN_PASSWORD_LENGTH)
      return "Re-enter your new password to confirm.";
    if (newPassword !== confirmPassword) return "New passwords do not match.";
    return null;
  }, [currentPassword, newPassword, confirmPassword, isSubmitting]);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);

    if (!currentPassword.trim()) {
      setError("Enter your current (temporary) password.");
      return;
    }
    if (newPassword.trim().length < MIN_PASSWORD_LENGTH) {
      setError(`New password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    try {
      setIsSubmitting(true);
      await resetPassword({ currentPassword, newPassword });
      setSuccess("Password updated. Redirecting…");
      await refreshUser();
      setTimeout(() => {
        // Refresh user state above so we don't bounce back here.
        navigate("/", { replace: true });
      }, 500);
    } catch (err) {
      console.error("Failed to reset password", err);
      setError("Failed to update password. Double-check your current password and try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1.5rem",
        background: "linear-gradient(180deg, #f8fafc, #ffffff)"
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "520px",
          border: "1px solid #e2e8f0",
          borderRadius: "1rem",
          boxShadow: "0 30px 60px rgba(15, 23, 42, 0.08)",
          padding: "1.5rem",
          background: "#fff"
        }}
      >
        <div style={{ fontWeight: 800, fontSize: "1.35rem" }}>Set a new password</div>
        <div style={{ marginTop: "0.35rem", color: "#64748b", fontSize: "0.95rem" }}>
          {user?.email ? `Signed in as ${user.email}. ` : null}
          You must change your temporary password before continuing.
        </div>

        <form onSubmit={onSubmit} style={{ marginTop: "1.25rem", display: "grid", gap: "0.85rem" }}>
          <div style={{ display: "grid", gap: "0.35rem" }}>
            <label style={{ fontWeight: 650, fontSize: "0.9rem" }}>Current password</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              style={{ padding: "0.65rem 0.75rem", borderRadius: "0.6rem", border: "1px solid #d0d7e2" }}
            />
          </div>

          <div style={{ display: "grid", gap: "0.35rem" }}>
            <label style={{ fontWeight: 650, fontSize: "0.9rem" }}>New password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              style={{ padding: "0.65rem 0.75rem", borderRadius: "0.6rem", border: "1px solid #d0d7e2" }}
            />
            <div style={{ fontSize: "0.8rem", color: "#64748b" }}>
              Minimum {MIN_PASSWORD_LENGTH} characters.
            </div>
          </div>

          <div style={{ display: "grid", gap: "0.35rem" }}>
            <label style={{ fontWeight: 650, fontSize: "0.9rem" }}>Confirm new password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              style={{ padding: "0.65rem 0.75rem", borderRadius: "0.6rem", border: "1px solid #d0d7e2" }}
            />
          </div>

          {error ? (
            <div style={{ color: "#b91c1c", background: "#fef2f2", border: "1px solid #fecaca", padding: "0.75rem", borderRadius: "0.75rem" }}>
              {error}
            </div>
          ) : null}
          {success ? (
            <div style={{ color: "#166534", background: "#f0fdf4", border: "1px solid #bbf7d0", padding: "0.75rem", borderRadius: "0.75rem" }}>
              {success}
            </div>
          ) : null}

          {disabledHint && !error ? (
            <div style={{ color: "#64748b", fontSize: "0.85rem" }}>{disabledHint}</div>
          ) : null}

          <button
            type="submit"
            disabled={!canSubmit}
            style={{
              marginTop: "0.25rem",
              padding: "0.75rem 1rem",
              borderRadius: "0.85rem",
              border: "none",
              background: canSubmit ? "linear-gradient(135deg, #2563eb, #4f46e5)" : "#cbd5e1",
              color: "#fff",
              fontWeight: 700,
              cursor: canSubmit ? "pointer" : "not-allowed"
            }}
          >
            {isSubmitting ? "Updating…" : "Update password"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;

